<template>
  <div v-if="salesperson && data" class="overflow-x-auto relative pb-4 h-full bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('salesperson.name')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-model="data.name"
            color="base"
            flat
            show-error-as-tooltip
            :error="validations?.name.required.$invalid ? validations?.name.required.$message : ''"
            :disabled="!data.isFromManager || mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-model="data.code"
            color="base"
            flat
            show-error-as-tooltip
            :error="validations?.code.required.$invalid ? validations?.code.required.$message : ''"
            :disabled="!data.isFromManager || mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.user')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="filteredUsers"
            :selected-item="selectedUser"
            name="user"
            :disable="!data.isFromManager"
            flat
            :mode="mode"
            show-error-as-tooltip
            :error="
              validations?.userId.required.$invalid ? validations?.userId.required.$message : ''
            "
            @update:selected="handleUserSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.type')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownSalespersonTypes"
            :selected-item="selectedType"
            name="type"
            :disable="!data.isFromManager"
            flat
            :mode="mode"
            show-error-as-tooltip
            :error="
              validations?.mwSalesPersonTypeId.required.$invalid
                ? validations?.mwSalesPersonTypeId.required.$message
                : ''
            "
            @update:selected="handleTypeSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.maxAccessLevel')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownSalesPersonsAccessLevels"
            :selected-item="selectedAccessLevel"
            name="accessLevel"
            :disable="!data.isFromManager"
            flat
            :mode="mode"
            show-error-as-tooltip
            :error="
              validations?.mwMaxLevelId.required.$invalid
                ? validations?.mwMaxLevelId.required.$message
                : ''
            "
            @update:selected="handleAccessLevelSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.onlineSearchLevelValue')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownOnlineSearchLevels"
            :selected-item="selectedOnlineSearchLevel"
            name="onlineSearchLevel"
            :disable="!data.isFromManager"
            flat
            :mode="mode"
            show-error-as-tooltip
            @update:selected="handleOnlineSearchLevelSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('salesperson.companySite')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="userCompanySites"
            :selected-item="selectedCompanySite"
            name="companySite"
            :disable="!data.isFromManager"
            flat
            :mode="mode"
            show-error-as-tooltip
            :error="
              validations?.companySiteId.required.$invalid
                ? validations?.companySiteId.required.$message
                : ''
            "
            @update:selected="handleCompanySiteSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle
            v-else
            :label="$t('baseObject.isActive')"
            name="isActive"
            v-model="data.isActive" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { enumToDropdownItems } from '@/utils/enums';
import { getCompanySitesByUserId } from '@/services/api/companies';

import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';

import { eOnlineSearchLevel, type SalesPerson } from '@/types/SalesPersons';
import type { SelectDropdownItem } from '@/types/Base';
import type { Validation } from '@vuelidate/core';

const { t: $t } = useI18n();

const { dropdownSalespersonTypes, dropdownSalesPersonsAccessLevels, users, userTypes } =
  storeToRefs(useLookupsStore());
const {
  getSalespersonTypesLookup,
  getSalesPersonsAccessLevelsLookup,
  getUsersLookup,
  getUserTypesLookup,
} = useLookupsStore();

const userCompanySites = ref<SelectDropdownItem[]>([]);

const filteredUsers = ref<SelectDropdownItem[]>([]);

const selectedUser = ref<SelectDropdownItem | null>(null);

const selectedCompanySite = ref<SelectDropdownItem | null>(null);

const selectedAccessLevel = ref<SelectDropdownItem | null>(null);

const selectedType = ref<SelectDropdownItem | null>(null);

const selectedOnlineSearchLevel = ref<SelectDropdownItem | null>(null);

const dropdownOnlineSearchLevels = ref(enumToDropdownItems(eOnlineSearchLevel));

defineProps<{
  salesperson?: SalesPerson;
  mode: 'view' | 'edit';
  validations?: Validation;
  isHorizontalLayout?: boolean;
}>();

const data = defineModel<SalesPerson>();

onMounted(async () => {
  await getSalespersonTypesLookup();
  await getSalesPersonsAccessLevelsLookup();
  await getUsersLookup();
  await getUserTypesLookup();
  const salesPersonUserTypeId = userTypes.value.find((t) => t.name === 'SalesPerson')?.id;
  if (salesPersonUserTypeId) {
    filteredUsers.value = users.value
      .filter((x) => x.mwUserTypeID === salesPersonUserTypeId)
      .map((item) => ({
        value: item.id,
        name: item.name,
      }));
  }
  await loadCompanySites();
  initDropdowns();
});

async function loadCompanySites() {
  if (data.value?.userID)
    userCompanySites.value = (await getCompanySitesByUserId(data.value.userID)).map((item) => ({
      value: item.id,
      name: item.name,
    }));
}

function initDropdowns() {
  if (!data.value) return;
  const typeValue = dropdownSalespersonTypes.value.find(
    (item) => item.value == data.value?.mwSalesPersonTypeID
  );
  if (typeValue) selectedType.value = typeValue;

  const userValue = filteredUsers.value.find((item) => item.value == data.value?.userID);
  if (userValue) selectedUser.value = userValue;

  const companySiteValue = userCompanySites.value.find(
    (item) => item.value == data.value?.companySiteID
  );
  if (companySiteValue) selectedCompanySite.value = companySiteValue;

  const salesPersonAccessLevelValue = dropdownSalesPersonsAccessLevels.value.find(
    (item) => item.value == data.value?.mwSalesPersonsAccessMaxLevelID
  );
  if (salesPersonAccessLevelValue) selectedAccessLevel.value = salesPersonAccessLevelValue;

  const onlineSearchLevelValue = dropdownOnlineSearchLevels.value.find(
    (item) => item.value == data.value?.onlineSearchLevel
  );
  if (onlineSearchLevelValue) selectedOnlineSearchLevel.value = onlineSearchLevelValue;
}

function handleAccessLevelSelection(item: SelectDropdownItem) {
  selectedAccessLevel.value = item ? item : null;
  if (data.value) {
    data.value.mwSalesPersonsAccessMaxLevelID = item ? item.value.toString() : undefined;
  }
}

function handleTypeSelection(item: SelectDropdownItem) {
  selectedType.value = item ? item : null;
  if (data.value) {
    data.value.mwSalesPersonTypeID = item ? item.value.toString() : undefined;
  }
}

function handleCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (data.value) {
    data.value.companySiteID = item ? item.value.toString() : undefined;
  }
}

function handleOnlineSearchLevelSelection(item: SelectDropdownItem) {
  selectedOnlineSearchLevel.value = item ? item : null;
  if (data.value) {
    if (item && (item.value == 1 || item.value == 2)) data.value.onlineSearchLevel = item.value;
    data.value.onlineSearchLevelValue = item ? item.name : undefined;
  }
}

async function handleUserSelection(item: SelectDropdownItem) {
  selectedUser.value = item ? item : null;
  if (data.value) {
    data.value.userID = item ? item.value.toString() : undefined;
  }
  await loadCompanySites();
}

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);
</script>
